import { useState, useEffect } from "react";
import { APP_NAME } from "../constants/app";
import { baseUrl } from "../auth/secret";

const DB_NAME = `${APP_NAME}-image-cache`;
const STORE_NAME = "images";
const DB_VERSION = 1;

const fetchingCache = new Map();
const compressionCache = new Map();

// Open or create the IndexedDB database
const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });
};
// Save image to IndexedDB
export const saveImageToDB = async (key, imageData) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const store = transaction.objectStore(STORE_NAME);
  const data = {
    image: imageData,
    timestamp: Date.now(),
  };
  store.put(data, key);
};
// Retrieve image from IndexedDB
export const getImageFromDB = async (key) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = () => {
      const data = request.result;
      resolve(data?.image || null);
    };
    request.onerror = () => reject(request.error);
  });
};

// Clear entries older than 24 hours
export const clearOldEntries = async (key) => {
  const db = await openDatabase();
  const transaction = db.transaction(STORE_NAME, "readwrite");
  const store = transaction.objectStore(STORE_NAME);
  const twentyFourHoursAgo = Date.now() - 24 * 60 * 60 * 1000;

  return new Promise((resolve, reject) => {
    const request = store.get(key);

    request.onsuccess = () => {
      const data = request.result;
      if (data?.timestamp && data.timestamp < twentyFourHoursAgo) {
        // Delete only this specific key if it's expired
        store.delete(key);
      }
      resolve();
    };

    request.onerror = () => reject(request.error);
  });
};

// Update getBase64Size to handle all base64 string formats and dimensions
export const getBase64Size = (base64String) => {
  const defaultResult = {
    sizeInKB: 0,
    sizeInMB: 0,
    sizeInBytes: 0,
    formattedSize: "0 Bytes",
    width: 0,
    height: 0,
    aspectRatio: 0,
  };

  try {
    // Handle empty or invalid input
    if (!base64String) {
      return defaultResult;
    }

    // Handle both data URL and raw base64
    let base64WithoutPrefix = base64String;
    if (base64String.includes(",")) {
      base64WithoutPrefix = base64String.split(",")[1];
    }

    // Clean up any whitespace
    base64WithoutPrefix = base64WithoutPrefix.trim();

    // Calculate padding
    const padding = base64WithoutPrefix.endsWith("==") ? 2 : base64WithoutPrefix.endsWith("=") ? 1 : 0;

    // Calculate actual length in bytes
    const base64Length = base64WithoutPrefix.length;
    const actualLength = Math.floor((base64Length * 3) / 4) - padding;

    // Convert to different units
    const sizeInKB = actualLength / 1024;
    const sizeInMB = sizeInKB / 1024;

    // Format the size string
    let formattedSize;
    if (sizeInMB >= 1) {
      formattedSize = `${sizeInMB.toFixed(2)} MB`;
    } else if (sizeInKB >= 1) {
      formattedSize = `${sizeInKB.toFixed(2)} KB`;
    } else {
      formattedSize = `${actualLength} Bytes`;
    }

    // Get dimensions synchronously if possible
    const img = new Image();
    img.src = base64String;

    return {
      sizeInKB: parseFloat(sizeInKB.toFixed(2)),
      sizeInMB: parseFloat(sizeInMB.toFixed(2)),
      sizeInBytes: actualLength,
      formattedSize,
      width: img.width || 0,
      height: img.height || 0,
      aspectRatio: img.width && img.height ? parseFloat((img.width / img.height).toFixed(2)) : 0,
    };
  } catch (error) {
    console.error("Error calculating base64 size:", error);
    return defaultResult;
  }
};

// Update compression presets for better quality/size balance
const COMPRESSION_PRESETS = {
  veryHigh: {
    targetSizeKB: 3072, // 3MB
    maxWidth: 2560,
    minQuality: 0.85,
    maxQuality: 0.95,
  },
  high: {
    targetSizeKB: 2048, // 2MB
    maxWidth: 1920,
    minQuality: 0.75,
    maxQuality: 0.9,
  },
  medium: {
    targetSizeKB: 1024, // 1MB
    maxWidth: 1600,
    minQuality: 0.6,
    maxQuality: 0.85,
  },
  low: {
    targetSizeKB: 512, // 500KB
    maxWidth: 1280,
    minQuality: 0.4,
    maxQuality: 0.75,
  },
  veryLow: {
    targetSizeKB: 256, // 250KB
    maxWidth: 1024,
    minQuality: 0.2,
    maxQuality: 0.6,
  },
};

// Update compressImage function to use cache
export const compressImage = async (base64String, options = {}) => {
  const { preset = "original", targetSizeKB, maxWidth, minQuality, maxQuality, quality } = options;

  // Create a cache key based on the image and compression options
  const cacheKey = `${base64String.slice(0, 100)}-${preset || "custom"}-${targetSizeKB || ""}-${maxWidth || ""}`;

  // Check compression cache first
  if (compressionCache.has(cacheKey)) {
    return compressionCache.get(cacheKey);
  }

  // Skip compression for original preset or SVG images
  if (preset === "original" || base64String.includes("image/svg+xml")) {
    return base64String;
  }

  const finalOptions = preset
    ? { ...COMPRESSION_PRESETS[preset] }
    : {
        targetSizeKB: targetSizeKB || 500,
        maxWidth: maxWidth || 1920,
        minQuality: minQuality || 0.1,
        maxQuality: maxQuality || 0.9,
        quality,
      };

  const compressionPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      try {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions maintaining aspect ratio
        if (width > finalOptions.maxWidth) {
          const ratio = finalOptions.maxWidth / width;
          width = finalOptions.maxWidth;
          height = Math.round(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d", { alpha: false });
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        // Direct quality compression if specified
        if (finalOptions.quality !== undefined) {
          const compressedBase64 = canvas.toDataURL("image/webp", finalOptions.quality);
          const result = compressedBase64;
          compressionCache.delete(cacheKey); // Clean up cache after compression
          resolve(result);
          return;
        }

        // Binary search for optimal compression
        let low = finalOptions.minQuality;
        let high = finalOptions.maxQuality;
        let bestResult = null;
        let bestQuality = finalOptions.maxQuality;
        let attempts = 0;
        const MAX_ATTEMPTS = 8; // Limit binary search iterations

        while (low <= high && attempts < MAX_ATTEMPTS) {
          attempts++;
          const mid = (low + high) / 2;
          const compressedBase64 = canvas.toDataURL("image/webp", mid);
          const size = getBase64Size(compressedBase64).sizeInKB;

          if (Math.abs(size - finalOptions.targetSizeKB) < 50 || high - low < 0.01) {
            bestResult = compressedBase64;
            break;
          }

          if (size > finalOptions.targetSizeKB) {
            high = mid;
          } else {
            bestResult = compressedBase64;
            bestQuality = mid;
            low = mid;
          }
        }

        const result = bestResult || canvas.toDataURL("image/webp", bestQuality);
        compressionCache.delete(cacheKey); // Clean up cache after compression
        resolve(result);
      } catch (error) {
        compressionCache.delete(cacheKey);
        reject(error);
      }
    };

    img.onerror = () => {
      compressionCache.delete(cacheKey);
      reject(new Error("Failed to load image"));
    };
    img.src = base64String;
  });

  // Store the promise in cache
  compressionCache.set(cacheKey, compressionPromise);
  return compressionPromise;
};

// Update getOrCreateFetchPromise to better handle compression caching
const getOrCreateFetchPromise = (url, compressionPreset) => {
  const cacheKey = `${url}-${compressionPreset}`;

  if (!fetchingCache.has(cacheKey)) {
    const promise = (async () => {
      try {
        // First check IndexedDB
        const cachedImage = await getImageFromDB(`cached-thumbnail-${url}`);
        if (cachedImage) {
          return cachedImage;
        }

        const response = await fetch(`${baseUrl}${url}`);
        const blob = await response.blob();

        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = async () => {
            try {
              const base64Data = reader.result;
              const compressedImage = compressionPreset === "original" ? base64Data : await compressImage(base64Data, { preset: compressionPreset });

              // Save to IndexedDB after successful compression
              await saveImageToDB(`cached-thumbnail-${url}`, compressedImage);
              resolve(compressedImage);
            } catch (error) {
              reject(error);
            } finally {
              fetchingCache.delete(cacheKey);
            }
          };
          reader.onerror = (error) => {
            fetchingCache.delete(cacheKey);
            reject(error);
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        fetchingCache.delete(cacheKey);
        throw error;
      }
    })();

    fetchingCache.set(cacheKey, promise);
  }

  return fetchingCache.get(cacheKey);
};

// Update the fetchThumbnail function in useCachedThumbnails
const fetchThumbnail = async (thumbnailUrl, compressionPreset) => {
  if (!thumbnailUrl) return null;
  try {
    const result = await getOrCreateFetchPromise(thumbnailUrl, compressionPreset);
    return result;
  } catch (error) {
    // TODO: Show error toast
    return null;
  }
};

// Thumbnails Image Array
export const useCachedThumbnails = (thumbnailUrls, compressionPreset) => {
  const [cachedThumbnails, setCachedThumbnails] = useState(Array.isArray(thumbnailUrls) ? Array(thumbnailUrls.length).fill(null) : null);
  useEffect(() => {
    if (!thumbnailUrls) return;

    let isMounted = true; // Add flag to track if component is mounted

    const fetchThumbnailsFromCache = async () => {
      try {
        const urls = Array.isArray(thumbnailUrls) ? thumbnailUrls : [thumbnailUrls];

        if (Array.isArray(thumbnailUrls)) {
          if (isMounted) {
            // Check if component is mounted before state update
            setCachedThumbnails(Array(urls.length).fill(null));
          }
        }

        urls.forEach(async (url, index) => {
          const cacheKey = `cached-thumbnail-${url}`;
          try {
            await clearOldEntries(cacheKey);

            const cached = await getImageFromDB(cacheKey);
            if (cached) {
              updateThumbnail(index, cached);
            } else {
              const fetched = await fetchThumbnail(url);
              updateThumbnail(index, fetched);
            }
          } catch (error) {
            console.error("Error fetching thumbnail:", error);
            updateThumbnail(index, null);
          }
        });
      } catch (error) {
        console.error("Error in fetch thumbnails:", error);
      }
    };

    const updateThumbnail = (index, thumbnail) => {
      if (!isMounted) return; // Don't update state if component is unmounted

      setCachedThumbnails((prev) => {
        if (Array.isArray(prev)) {
          const newThumbnails = [...prev];
          newThumbnails[index] = thumbnail;
          return newThumbnails;
        }
        return thumbnail;
      });
    };

    fetchThumbnailsFromCache();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [thumbnailUrls, compressionPreset]);

  return cachedThumbnails;
};

// Download image function
export const downloadImage = async (imageData, fileName, compressionPreset) => {
  try {
    if (!imageData) {
      const link = document.createElement("a");
      link.href = "https://placehold.co/600x400?text=No+Image";
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    if (typeof imageData === "string" && imageData.startsWith("data:")) {
      const link = document.createElement("a");
      link.href = compressionPreset === "original" ? imageData : await compressImage(imageData, { preset: compressionPreset });
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      const url = imageData.startsWith("http") || imageData.startsWith("https") ? imageData : `${baseUrl}${imageData}`;
      const response = await fetch(url);
      const blob = await response.blob();

      if (compressionPreset === "original") {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(link.href);
        document.body.removeChild(link);
      } else {
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Data = reader.result;
          const compressedImage = await compressImage(base64Data, { preset: compressionPreset });
          const link = document.createElement("a");
          link.href = compressedImage;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        };
        reader.readAsDataURL(blob);
      }
    }
  } catch (error) {
    console.error("Error downloading image:", error);
  }
};
